import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams, usePathname, useSearchParams } from 'next/navigation';

import { RQ_BRANDS_BATCH_BY_SLUG_KEY } from '../ReactQuery/cacheKeys';
import { ActivityTrackerEventType } from '../activityTracker/constants';
import { useActivityTracker } from '../activityTracker/useActivityTracker';
import { fetchBrandsBatchBySlugs } from '../brands/api';
import { useBrands } from '../brands/hooks/useBrands';
import { DEPARTMENT_TO_GENDER, kidswearId } from '../categories/constants';
import { buildPathParamsUrl, parseFiltersFromUrl } from '../filters/helpers';
import { useCurrentLocation } from '../location/useCurrentLocation';
import { useExtendedRouter } from '../routing/useExtendedRouter';
import { useCurrentUser } from '../user/useCurrentUser';

import { SORT_BY_OPTIONS } from './constants';
import { getIsPathParamsMode } from './helpers';
import { type SortBy } from './types';
import { useCombinedFilters } from './useCombinedFilters';

export type SearchFilters = {
  brands?: string[];
  isDiscounted?: boolean;
  priceMin?: number;
  priceMax?: number;
  sizes?: string[];
  colours?: string[];
  conditions?: string[];
  sort?: SortBy;
  discountTypes?: string;
  minDiscount?: string;
  maxDiscount?: string;
  shippingId?: string;
  groups?: string;
  productTypes?: string[];
  isKids?: boolean;
  gender?: string;
};

const emptyFilterState = {
  isDiscounted: false,
  sort: SORT_BY_OPTIONS[0].id,
};

function sortNumericAscending<T>(a: T, b: T) {
  return Number(a) > Number(b) ? 1 : -1;
}

function shouldTriggerBrowsePageChange({
  newFilterState,
  searchFilters,
  pathname,
  url,
  location,
}: {
  newFilterState: SearchFilters;
  searchFilters: SearchFilters;
  pathname: string;
  url: string;
  location: string;
}) {
  /*
   * There are three situations in which we should make a hard route change when in
   * path params mode.
   * 1. When we move to a different type of browse page e.g Brand -> brand + category
   * 2. When we add or remove a category within the category or brand + category page
   * 3. When we add or remove a subcategory within the category or brand + category page
   * Outside of these we should shallow route as normal for a simple filter update to
   * avoid the heavy round trip to the server when not needed
   */
  // eslint-disable-next-line prefer-destructuring
  let pagePath = pathname.split('/')[1];
  // eslint-disable-next-line prefer-destructuring
  let newPagePath = url.split('/')[1];

  if (location !== process.env.NEXT_PUBLIC_DEFAULT_LOCATION) {
    // eslint-disable-next-line prefer-destructuring
    pagePath = pathname.split('/')[2];
    // eslint-disable-next-line prefer-destructuring
    newPagePath = url.split('/')[2];
  }

  if (pagePath !== newPagePath) {
    return true;
  }

  if (
    newFilterState.groups !== searchFilters.groups &&
    pagePath !== 'explore'
  ) {
    return true;
  }

  if (
    JSON.stringify(newFilterState.productTypes) !==
      JSON.stringify(searchFilters.productTypes) &&
    pagePath !== 'explore'
  ) {
    return true;
  }

  return false;
}

function updateParam(
  params: URLSearchParams,
  name: string,
  value?: string[] | string
): void {
  if (!value || value.length === 0) {
    return params.delete(name);
  }
  if (value instanceof Array) {
    return params.set(name, value.join(','));
  }
  return params.set(name, value);
}

export function useSearchFilters() {
  const searchParams = useSearchParams();
  const params = useParams();
  const slug = params?.slug;
  const pathname = usePathname();
  const [, user] = useCurrentUser();
  const router = useExtendedRouter();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const isPathParamsMode = getIsPathParamsMode(pathname);
  const { data: brandsWithIds } = useBrands({ enabled: isPathParamsMode });
  const { data: brandsBySlugData } = useQuery({
    queryKey: [RQ_BRANDS_BATCH_BY_SLUG_KEY, { slug: [slug] }],
    queryFn: () => fetchBrandsBatchBySlugs({ brandSlugs: [slug as string] }),
    enabled: !!slug,
  });
  const [filtersModified, setFiltersModified] = useState(false);

  const { location } = useCurrentLocation();

  const filtersFromUrl = parseFiltersFromUrl({
    params: searchParams,
    pathname,
    isPathParamsMode,
    brandData: brandsBySlugData?.data,
  });

  const searchFilters = useCombinedFilters(filtersFromUrl, filtersModified);

  function pushFilterStateToUrl(newFilterState: SearchFilters) {
    const queryParams = new URLSearchParams(Array.from(searchParams.entries()));

    const {
      isDiscounted,
      priceMin,
      priceMax,
      sizes,
      colours,
      conditions,
      sort,
      brands,
      groups,
      productTypes,
      isKids,
      gender,
    } = newFilterState;

    /** set all filters as query params by default */
    updateParam(queryParams, 'isDiscounted', isDiscounted ? 'true' : undefined);
    updateParam(queryParams, 'priceMin', priceMin?.toString());
    updateParam(queryParams, 'priceMax', priceMax?.toString());
    updateParam(queryParams, 'sizes', sizes);
    updateParam(queryParams, 'colours', colours);
    updateParam(queryParams, 'conditions', conditions);
    updateParam(queryParams, 'sort', sort);
    updateParam(queryParams, 'brands', brands);
    updateParam(queryParams, 'groups', groups);
    updateParam(queryParams, 'productTypes', productTypes);
    updateParam(queryParams, 'isKids', isKids ? 'true' : undefined);
    updateParam(queryParams, 'gender', gender);

    setFiltersModified(true);

    let url = `${pathname}?${queryParams.toString()}`;

    /**
     * in path params mode, we conditionally replace some query params with
     * path segments in the URL
     */
    if (isPathParamsMode) {
      url = buildPathParamsUrl({
        queryParams,
        brands,
        groups,
        productTypes,
        gender,
        isKids,
        brandsWithIds: brandsWithIds?.data,
      });

      if (
        shouldTriggerBrowsePageChange({
          newFilterState,
          searchFilters,
          pathname,
          url,
          location,
        })
      ) {
        return router.push(url);
      }
    }

    return window.history.pushState(null, '', url);
  }

  function updateMultiSelectFilter(
    name: 'brands' | 'sizes' | 'colours' | 'conditions',
    value?: string
  ): void {
    if (!value) {
      return pushFilterStateToUrl({ ...searchFilters, [name]: undefined });
    }

    const newFilterState = { ...searchFilters };
    const currentFilterValue = newFilterState[name] ?? [];

    if (currentFilterValue.includes(value)) {
      currentFilterValue.splice(currentFilterValue.indexOf(value), 1);
    } else {
      currentFilterValue.push(value);
    }

    if (currentFilterValue.length === 0) {
      delete newFilterState[name];
    } else {
      newFilterState[name] = currentFilterValue.sort(sortNumericAscending);
    }

    return pushFilterStateToUrl(newFilterState);
  }

  function setGroupsFilter(
    selectedDepartment?: string,
    selectedGroup?: string
  ) {
    const newFilterState = { ...searchFilters };
    const newGender = selectedDepartment
      ? DEPARTMENT_TO_GENDER[selectedDepartment]
      : undefined;
    const isDeselected =
      selectedGroup === searchFilters.groups &&
      searchFilters.gender === newGender;

    if (!selectedDepartment || !selectedGroup || isDeselected) {
      delete newFilterState.gender;
      delete newFilterState.groups;
      delete newFilterState.productTypes;
      delete newFilterState.isKids;

      return pushFilterStateToUrl(newFilterState);
    }

    newFilterState.groups = selectedGroup;
    newFilterState.isKids = selectedDepartment === kidswearId;

    if (newGender) {
      newFilterState.gender = newGender;
    } else {
      delete newFilterState.gender;
    }

    delete newFilterState.productTypes;

    pushFilterStateToUrl(newFilterState);
  }

  function setProductTypeFilter(value?: string) {
    const newFilterState = { ...searchFilters };

    if (!value) {
      delete newFilterState.productTypes;
      return pushFilterStateToUrl(newFilterState);
    }

    if (newFilterState.productTypes?.includes(value)) {
      newFilterState.productTypes = newFilterState.productTypes?.filter(
        (subcat) => subcat !== value
      );
    } else {
      newFilterState.productTypes = newFilterState.productTypes
        ? [...newFilterState.productTypes, value].sort(sortNumericAscending)
        : [value];
    }

    pushFilterStateToUrl(newFilterState);
  }

  function setBrandFilter(value?: string) {
    updateMultiSelectFilter('brands', value);
  }

  function setIsDiscountedFilter(isDiscounted: boolean) {
    pushFilterStateToUrl({ ...searchFilters, isDiscounted });
  }

  function setPriceFilter({
    priceMin,
    priceMax,
  }: {
    priceMin?: number;
    priceMax?: number;
  }) {
    const newFilterState = { ...searchFilters };

    /**
     * If priceMin or priceMax are present, set those values
     * Unset either if missing
     */
    if (priceMin && priceMax) {
      newFilterState.priceMin = priceMin;
      newFilterState.priceMax = priceMax;
    } else if (!priceMax && !priceMin) {
      delete newFilterState.priceMin;
      delete newFilterState.priceMax;
    } else if (priceMin) {
      newFilterState.priceMin = priceMin;
      delete newFilterState.priceMax;
    } else if (priceMax) {
      delete newFilterState.priceMin;
      newFilterState.priceMax = priceMax;
    }

    pushFilterStateToUrl(newFilterState);
  }

  function setSizeFilter(value?: string) {
    updateMultiSelectFilter('sizes', value);
  }

  function setColourFilter(value?: string) {
    updateMultiSelectFilter('colours', value);
  }

  function setConditionFilter(value?: string) {
    updateMultiSelectFilter('conditions', value);
  }

  function setSortFilter(sort: SortBy) {
    pushFilterStateToUrl({ ...searchFilters, sort });
  }

  function clearAll() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CLEAR_FILTER_ACTION, {
      schemaVersion: '2.0',
    });

    pushFilterStateToUrl({ ...emptyFilterState });
  }

  return {
    searchFilters,
    setSearchFilters: {
      setGroupsFilter,
      setProductTypeFilter,
      setBrandFilter,
      setIsDiscountedFilter,
      setPriceFilter,
      setSizeFilter,
      setColourFilter,
      setConditionFilter,
      setSortFilter,
      clearAll,
    },
  };
}
